import {
  MOCK_DATA_MODEL_FIELDS_RESPONSE,
  MOCK_MODEL_RELEASE_ID,
  MOCK_VALUE_TYPE_BY_CATEGORY,
} from "../chartStudio/chartStudioDataModel.mock";
import type { FieldSummaryApi } from "../chartStudio/chartStudioDataModel.types";
import { mockConflictError } from "../mockDomainError";
import { mockFetchCanvasItem } from "./storyCanvasApi.mock";
import { assertStoryExists, listMockStoryFilterRows } from "./storyFilters.mock";
import { findMockFieldHierarchy } from "./storyFilters.mockRules";
import type {
  StoryFieldApi,
  StoryFieldListParams,
  StoryFieldListResponseApi,
} from "./storyFilters.types";
import type { DataModelFieldCategory } from "../../components/registry/dataModelFieldSections";

/**
 * Mock `/fields`. Built from the Chart Studio catalog so both panels agree on
 * names and ids; the picker rules (scope, identifiers, used-first, the active
 * filter) are the backend's, written here so the UI can run offline.
 */

const LATENCY_MS = 150;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** The catalog's categories, in the order the picker lists them. */
const CATEGORY_ORDER: DataModelFieldCategory[] = [
  "dates",
  "dimensions",
  "geographic",
  "measures",
  "calculated",
];

/** What the editable chart (`widget-chart-1`) is bound to. */
const USED_FIELD_IDS: ReadonlySet<string> = new Set(["region", "sales"]);

/** A key field (one value per row): offered only when `include_identifiers` is set. */
const IDENTIFIER: FieldSummaryApi = { id: "order_id", kind: "dimension_attribute", name: "Order ID" };


interface CatalogRow {
  summary: FieldSummaryApi;
  category: DataModelFieldCategory;
  isIdentifier: boolean;
}

const catalogRows = (): CatalogRow[] => [
  ...CATEGORY_ORDER.flatMap((category) =>
    MOCK_DATA_MODEL_FIELDS_RESPONSE[category].map((summary) => ({ summary, category, isIdentifier: false })),
  ),
  { summary: IDENTIFIER, category: "dimensions", isIdentifier: true },
];

/** The id of a filter already on this field in the scope asked for: the story's global filters, or that chart's `local` ones. */
const activeFilterId = (fieldId: string, canvasItemId: string | undefined): string | null =>
  listMockStoryFilterRows().find(
    (filter) =>
      filter.field.model_field_id === fieldId &&
      (canvasItemId
        ? filter.kind === "local" && filter.canvas_item_id === canvasItemId
        : filter.kind === "global"),
  )?.id ?? null;

const toStoryField = (row: CatalogRow, canvasItemId: string | undefined): StoryFieldApi => ({
  field: { kind: row.summary.kind, model_field_id: row.summary.id },
  model_release_id: MOCK_MODEL_RELEASE_ID,
  name: row.summary.name,
  category: row.category,
  value_type: MOCK_VALUE_TYPE_BY_CATEGORY[row.category],
  used_in_story: USED_FIELD_IDS.has(row.summary.id),
  local_only: row.summary.kind === "measure",
  active_filter_id: activeFilterId(row.summary.id, canvasItemId),
  hierarchy: findMockFieldHierarchy(row.summary.id),
});

const matchesSearch = (row: CatalogRow, search: string | undefined): boolean => {
  const needle = search?.trim().toLowerCase();
  if (!needle) return true;
  return (
    row.summary.name.toLowerCase().includes(needle) || row.summary.id.toLowerCase().includes(needle)
  );
};

/** A KPI has no field to filter on; the item GET resolves the item or throws the 404. */
const assertChartItem = async (storyId: string, canvasItemId: string): Promise<void> => {
  const item = await mockFetchCanvasItem(storyId, canvasItemId);
  if (item.item_type === "kpi" || item.item_type === "kpi_2") {
    throw mockConflictError(`A KPI has no fields to filter on: ${canvasItemId}`);
  }
};

export async function mockFetchStoryFields(
  storyId: string,
  params: StoryFieldListParams = {},
): Promise<StoryFieldListResponseApi> {
  await wait(LATENCY_MS);
  assertStoryExists(storyId);
  const { canvasItemId, kinds, usedOnly, includeIdentifiers, search } = params;
  if (canvasItemId) await assertChartItem(storyId, canvasItemId);

  const fields = catalogRows()
    // Story-wide filters take no measures; a chart's own filters may.
    .filter((row) => canvasItemId || row.summary.kind !== "measure")
    .filter((row) => includeIdentifiers || !row.isIdentifier)
    .filter((row) => !kinds?.length || kinds.includes(row.summary.kind))
    .filter((row) => matchesSearch(row, search))
    .map((row) => toStoryField(row, canvasItemId))
    .filter((field) => !usedOnly || field.used_in_story);

  // Stable: used fields first, then the catalog's own order.
  const used = fields.filter((field) => field.used_in_story);
  return { fields: [...used, ...fields.filter((field) => !field.used_in_story)] };
}
